import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

import { GameCenterFormTypes } from "configs/types";
import { useAppSelector, useAppDispatch } from "store/index";
import { GameItem, handleClearItem } from "store/gameCenter/games/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import SearchInput from "components/inputs/SearchInput/SearchInput";
import TagsOrderListTabItem from "./TagsOrderListTabItem";
import Button from "../../../../components/buttons/Button";
import { Link, useNavigate } from "react-router-dom";
import ROUTES from "../../../../router/routes";
import { closeModal } from "../../../../store/modals/slice";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import Pagination from "components/Pagination";
import { Checkbox } from "components/table/components/Checkbox/Checkbox";
import { handleInvalidateList as handleInvalidateTagsList } from "store/gameCenter/tags/slice";
import { handleInvalidateList as handleInvalidateCollectionsList } from "store/gameCenter/collections/slice";

const gamesPerPageOptions = [15, 50, 100, 250, 500, 1000];
const defaultGamesPerPage = 50;

type Props = {
  type: GameCenterFormTypes;
  slug: string;
  id: number;
  isReadOnly?: boolean;
};

const TagsOrderListTab: React.FC<Props> = ({
  type,
  slug,
  id,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [gamesList, setGamesList] = useState<GameItem[]>([]);
  const [priorityGames, setPriorityGames] = useState<GameItem[]>([]);
  const [activeGeo, setActiveGeo] = useState<string>();
  const [isFetched, setIsFetched] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [currentLimit, setCurrentLimit] = useState<number>(
    defaultGamesPerPage,
  );
  const [totalItems, setTotalItems] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [selectedGameIds, setSelectedGameIds] = useState<number[]>([]);
  const [isRemovingGames, setIsRemovingGames] = useState<boolean>(false);

  const geoList = useAppSelector(selectFilteredGeo);

  useEffect(() => {
    const firstGeo = geoList[0];

    if (firstGeo) {
      setActiveGeo(firstGeo);
    }
  }, [geoList]);

  const handleLoadGames = async () => {
    setIsFetched(false);
    try {
      const result = await instance.post(
        `${type === GameCenterFormTypes.collection ? apiRoutes.collectionGamesList : apiRoutes.tagGamesList}`,
        {
          scopeSlugs: [slug],
          limit: currentLimit,
          offset: activePage - 1,
          geo: activeGeo,
          gameTitle: debouncedSearchValue || undefined,
        },
      );
      setGamesList(result.data.items ?? []);
      setSelectedGameIds([]);
      setTotalItems(result.data.totalItems ?? 0);
      setTotalPages(result.data.totalPages ?? 1);
    } catch (error) {
      console.log(error, "error");
    } finally {
      setIsFetched(true);
    }
  };

  const handleLoadPriorityGames = async () => {
    try {
      const result = await instance.post(
        `${type === GameCenterFormTypes.collection ? apiRoutes.collectionGamesList : apiRoutes.tagGamesList}`,
        {
          scopeSlugs: [slug],
          limit: 10000,
          offset: 0,
          geo: activeGeo,
        },
      );
      setPriorityGames(result.data.items ?? []);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleRemoveGames = async (gameIds: number[]) => {
    if (isReadOnly || !gameIds.length || isRemovingGames) {
      return;
    }

    setIsRemovingGames(true);
    try {
      await instance.put(
        type === GameCenterFormTypes.tag
          ? `${apiRoutes.tagRemoveGames}/${id}/remove-games`
          : `${apiRoutes.collectionRemoveGames}/${id}/remove-games`,
        {
          gameIds,
          geos: [activeGeo],
        },
      );

      const nextTotalItems = Math.max(0, totalItems - gameIds.length);
      const nextTotalPages = Math.max(
        1,
        Math.ceil(nextTotalItems / currentLimit),
      );

      setGamesList((prev) => prev.filter((g) => !gameIds.includes(g.id)));
      setSelectedGameIds([]);
      setTotalItems(nextTotalItems);
      setTotalPages(nextTotalPages);

      dispatch(
        type === GameCenterFormTypes.tag
          ? handleInvalidateTagsList()
          : handleInvalidateCollectionsList(),
      );

      if (activePage > nextTotalPages) {
        setActivePage(nextTotalPages);
      } else {
        handleLoadGames();
      }
      handleLoadPriorityGames();

      dispatch(
        showNotification({
          title: t("success"),
          message: t("gameRemoved"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("gameRemoveError"),
          type: NotificationTypes.error,
        }),
      );
    } finally {
      setIsRemovingGames(false);
    }
  };

  const handleSelectGame = (gameId: number) => {
    if (isReadOnly) return;

    setSelectedGameIds((selectedIds) =>
      selectedIds.includes(gameId)
        ? selectedIds.filter((id) => id !== gameId)
        : [...selectedIds, gameId],
    );
  };

  const handleSelectAllGames = () => {
    if (isReadOnly) return;

    setSelectedGameIds((selectedIds) =>
      selectedIds.length === gamesList.length
        ? []
        : gamesList.map((game) => game.id),
    );
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActivePage(1);
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    if (
      (type === GameCenterFormTypes.collection ||
        type === GameCenterFormTypes.tag) &&
      activeGeo
    ) {
      handleLoadGames();
    }
  }, [
    type,
    slug,
    activeGeo,
    activePage,
    currentLimit,
    debouncedSearchValue,
  ]);

  useEffect(() => {
    if (
      (type === GameCenterFormTypes.collection ||
        type === GameCenterFormTypes.tag) &&
      activeGeo
    ) {
      handleLoadPriorityGames();
    }
  }, [type, slug, activeGeo]);

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const moveItem = async (gameId: number, targetPriority: number) => {
    if (isReadOnly) return;

    const items = Array.from(priorityGames);
    const currentIndex = items.findIndex((game) => game.id === gameId);
    if (currentIndex < 0) {
      return;
    }

    const targetIndex = Math.max(
      0,
      Math.min(targetPriority - 1, items.length - 1),
    );
    if (currentIndex === targetIndex) {
      return;
    }

    const [reorderedItem] = items.splice(currentIndex, 1);
    items.splice(targetIndex, 0, reorderedItem);
    setPriorityGames(items);
    setGamesList(items);
    setIsFetched(false);
    const games: Record<number, number> = {};
    items.forEach((el, idx) => {
      games[el.id] = idx;
    });
    try {
      await instance.put(
        `${type === GameCenterFormTypes.tag ? apiRoutes.tagGamesOrder : apiRoutes.collectionGamesOrder}/${id}/priorities-games`,
        {
          geos: [activeGeo],
          gamesPriorities: games,
        },
      );
      dispatch(
        showNotification({
          title: t("success"),
          message: t("itemsOrderUpdated"),
          type: NotificationTypes.success,
        }),
      );
    } catch {
      dispatch(
        showNotification({
          title: t("error"),
          message: t("itemsOrderUpdateError"),
          type: NotificationTypes.error,
        }),
      );
    }
    await Promise.all([handleLoadGames(), handleLoadPriorityGames()]);
  };

  const goToGames = () => {
    navigate(`${ROUTES.GAME_CENTER_GAMES_LIST_ACTIVE}`);
    dispatch(closeModal());
    dispatch(handleClearItem());
  };

  const hasGames = totalItems > 0;
  const getPriority = (gameId: number) =>
    priorityGames.findIndex((game) => game.id === gameId) + 1;

  return (
    <div
      className={`games-order-wrapper ${isReadOnly ? "is-read-only" : ""}`}
    >
      <div className="game-modal-tabs small">
        {geoList.map((el) => (
          <div
            className={`tab-item ${activeGeo === el ? "active" : ""}`}
            key={el}
            onClick={() => {
              setIsFetched(false);
              setGamesList([]);
              setActiveGeo(el);
              setActivePage(1);
              setSearchValue("");
            }}
          >
            {el.toLowerCase() === "ww" ? (
              <span className="mr-5">🌎</span>
            ) : (
              <img
                src={`https://flagcdn.com/w40/${el.toLowerCase()}.png`}
                alt={el}
              />
            )}
            {t(el)}
          </div>
        ))}
      </div>

      <div className="games-order-search">
        <SearchInput
          placeholder={t("searchGame")}
          value={searchValue}
          onChange={onSearch}
          isSmall
        />
      </div>

      <div className="games-order-list">
        <div className="item game-order-item">
          <div className="game-selection-checkbox">
            <Checkbox
              checked={
                gamesList.length > 0 &&
                selectedGameIds.length === gamesList.length
              }
              disabled={isReadOnly || !gamesList.length}
              onClick={handleSelectAllGames}
            />
          </div>
          <div className="order">{t("priority")}</div>
          <div className="show">{t("show")}</div>
          <div className="image">{t("image")}</div>
          <div className="content">{t("game")}</div>
          <div className="provider">{t("provider")}</div>
          <div className="devices">{t("devices")}</div>
          <div className="devices">{t("options")}</div>
        </div>

        {!isFetched ? (
          <ModalTabSkeleton />
        ) : (
          <>
            {gamesList.map((el, index) => {
              return (
                <TagsOrderListTabItem
                  key={el.id}
                  index={index}
                  priority={getPriority(el.id)}
                  item={el}
                  moveItem={moveItem}
                  onRemove={(gameId) => handleRemoveGames([gameId])}
                  isSelected={selectedGameIds.includes(el.id)}
                  onSelect={handleSelectGame}
                  isReadOnly={isReadOnly}
                />
              );
            })}

          </>
        )}
      </div>

      {isFetched && hasGames && (
        <Pagination
          activePage={activePage}
          limit={currentLimit}
          totalItems={totalItems}
          totalPages={totalPages}
          limitsArray={gamesPerPageOptions}
          handleLimitClick={(limit) => {
            setCurrentLimit(limit);
            setActivePage(1);
          }}
          handlePrevClick={() =>
            setActivePage((page) => Math.max(1, page - 1))
          }
          handleNextClick={() =>
            setActivePage((page) => Math.min(totalPages, page + 1))
          }
        />
      )}

      {isFetched && (
        <div className={hasGames ? "games-add-bottom" : "games-add-center"}>
          <Button
            handleClick={
              selectedGameIds.length
                ? () => handleRemoveGames(selectedGameIds)
                : goToGames
            }
            isLoad={isRemovingGames}
            className={`games-list-action-button ${
              selectedGameIds.length ? "games-list-action-button--remove" : ""
            }`}
            txt={selectedGameIds.length ? t("removeGames") : t("addGames")}
            disabled={isReadOnly}
          />
        </div>
      )}
    </div>
  );
};

export default TagsOrderListTab;
